import { formatISO } from "date-fns";
import format from "date-fns/format";
import { action, flow, observable } from "mobx";
import { observer, Observer } from "mobx-react";
import { Button } from "primereact/button";
import React, { useContext } from "react";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import { AddEducationToCandidateResponse } from "../../../../api/candidate/addEducationToCandidate";
import { useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import { redirect } from "../../../../WSUtils/urlUtils";
import {
  invalidateQueries,
  useLatestQuery,
} from "../../../../WSUtils/useLatestQuery";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { concatClassnames, defDFnsOpt } from "../../../../WSUtils/util";
import {
  CVPSearchParams,
  queryStringStore,
} from "../../../../WSUtils/wsQueryStringStore";
import { sentryAssertData } from "../../../../WSUtils/wsSentry";
import { CVEditContext } from "../../CVEditContext";
import { CVEditListStyles } from "../../CVEditListStyles";
import { CVEditEducation } from "./CVEditEducation";
import { onCveEducationEditorCloseButtonClick } from "./CVEditEducationEditor";
import { CVEditEducationSegmentStore } from "./CVEditEducationSegmentStore";

export interface CVEditEducationListProps {
  educationSegmentStore: CVEditEducationSegmentStore;
}

export const CVEditEducationList = observer(
  ({ educationSegmentStore }: CVEditEducationListProps) => {
    const cvEditContext = useContext(CVEditContext);
    const userInfo = useContext(UserInfoContext);
    const queryClient = useQueryClient();
    const L = useLocStore(LocContext.CV);

    const deletedItems = educationSegmentStore.deletedListItems;
    const items = educationSegmentStore.listItems;

    const { data: educationTypes } = useLatestQuery(
      ["typesAPI.getTypes(utdannelse)"],
      () =>
        userInfo.fullAPI.typesAPI.getTypes({
          userInfo,
          group: "utdannelse",
        })
    );

    sentryAssertData(educationTypes, { userInfo });

    const getEducationTypeText = (cveEdu: CVEditEducation) =>
      educationTypes?.mapped.find((u) => u.value === cveEdu.educationTypeId)
        ?.label;

    if (educationSegmentStore.nTotalListItems === 0) return null;

    const deletedItemsComponent = (
      <Observer
        render={() => (
          <>
            {deletedItems.map((cveEdu: CVEditEducation) => (
              <div
                key={cveEdu.id}
                className={concatClassnames([
                  "cve-listing",
                  "cve-listing-deleted",
                ])}
              >
                <div className="p-inline-message p-component p-inline-message-info">
                  <div aria-live="polite" role="alert">
                    <span className="p-inline-message-icon pi pi-info-circle"></span>
                    <span className="p-inline-message-text">
                      {L.id("8337ce3ec0a3").loc("Slettet")}
                    </span>
                  </div>
                  <Button
                    className="p-button-text"
                    label={L.id("9de3b60370ab").loc("Angre")}
                    onClick={(evt) => {
                      evt.preventDefault();

                      const candidateAPI = userInfo.fullAPI.candidateAPI;

                      const restore = flow(function* () {
                        const result: AddEducationToCandidateResponse =
                          yield candidateAPI.addEducationToCandidate({
                            showSentryDialogOnError: true,
                            userInfo,
                            firmId: userInfo.idFirm,

                            comment: cveEdu.description,
                            from: cveEdu.from ? formatISO(cveEdu.from) : null,
                            to: cveEdu.to ? formatISO(cveEdu.to) : null,
                            educationType: cveEdu.educationTypeId,
                            expirationDate: null,
                            school: cveEdu.school,

                            //
                            duplicateCheck: false,
                            references: null,
                          });

                        if (result.ok) {
                          yield invalidateQueries(queryClient, [
                            "candidateAPI.getEducationForCandidate",
                          ]);

                          educationSegmentStore.deletedEducations.delete(
                            cveEdu.id
                          );
                        }
                      });

                      restore();

                      return false;
                    }}
                  />
                </div>
              </div>
            ))}
          </>
        )}
      />
    );

    const itemsComponent = (
      <Observer
        render={() => (
          <>
            {items.map((cveEdu) => (
              <div
                className={concatClassnames(["cve-listing"])}
                key={cveEdu.id}
              >
                <div className="cve-listing-left">
                  <div className="cve-education-listing-dates cve-left-dates">
                    <span className="cve-education-listing-startdate cve-listing-text">
                      {!!cveEdu.from &&
                        format(cveEdu.from, "MM.yyyy", defDFnsOpt)}
                    </span>
                    <span> - </span>
                    <div className="cve-education-listing-enddate cve-listing-text">
                      {!!cveEdu.to && format(cveEdu.to, "MM.yyyy", defDFnsOpt)}
                      {!cveEdu.to &&
                        L.id("c0c5286dd6ec").loc("Pågående utdanning")}
                    </div>
                  </div>
                </div>
                <div className="cve-listing-right">
                  <div className="cve-listing-buttons">
                    <Buttons
                      cveEdu={cveEdu}
                      educationSegmentStore={educationSegmentStore}
                    />
                  </div>

                  <div
                    className="cve-education-listing-education-type cve-listing-text"
                    title={L.id("e0c8ec744641").loc("Type utdannelse")}
                  >
                    {getEducationTypeText(cveEdu)}
                  </div>

                  <div
                    className="cve-education-listing-school cve-listing-text"
                    title={L.id("640c035d6e37").loc("Skole")}
                  >
                    {cveEdu.school}
                  </div>

                  <div
                    className="cve-education-listing-description cve-listing-text"
                    title={L.id("ac20486dbdc3").loc("Beskrivelse")}
                  >
                    {cveEdu.description}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      />
    );

    return (
      <CVEditEducationListStyles
        className={concatClassnames([
          "cve-list cve-education-list cve-two-column-list",
          cvEditContext.layoutClassName,
        ])}
      >
        {deletedItemsComponent}
        {itemsComponent}
      </CVEditEducationListStyles>
    );
  }
);

interface ButtonsProps {
  cveEdu: CVEditEducation;
  educationSegmentStore: CVEditEducationSegmentStore;
}

const Buttons = ({ cveEdu, educationSegmentStore }: ButtonsProps) => {
  const cvEditContext = useContext(CVEditContext);
  const userInfo = useContext(UserInfoContext);
  const L = useLocStore(LocContext.CV);

  const queryClient = useQueryClient();

  const savingUsingToggleButton = observable.box(false);

  return (
    <Observer
      render={() => {
        const isEditingThis =
          educationSegmentStore.editingEducationId === cveEdu.id;

        return (
          <>
            <Button
              icon="pi pi-trash"
              title={L.id("e8e34ac72d21").loc("Fjern utdanning")}
              className="p-button-rounded p-button-text p-button-icon-only"
              onClick={flow(function* (evt) {
                evt.preventDefault();

                educationSegmentStore.deletedEducations.set(cveEdu.id, cveEdu);

                if (queryStringStore.qs[CVPSearchParams.cvpedu] === cveEdu.id) {
                  redirect(null, {
                    userInfo,
                    mergeQs: { [CVPSearchParams.cvpedu]: null },
                  });
                }

                const result =
                  yield userInfo.fullAPI.candidateAPI.removeEducationFromCandidate(
                    {
                      showSentryDialogOnError: true,
                      educationId: Number(cveEdu.id),
                      firmId: userInfo.idFirm,
                      userInfo,
                    }
                  );

                yield invalidateQueries(queryClient, [
                  "candidateAPI.getEducationForCandidate",
                ]);

                return false;
              })}
            />
            <Button
              icon="pi pi-pencil"
              title={L.id("d7efa2d237dd").loc("Rediger utdanning")}
              className={concatClassnames([
                "p-button-rounded p-button-icon-only",
                !isEditingThis && "p-button-text",
              ])}
              loading={savingUsingToggleButton.get()}
              onClick={action((evt) => {
                evt.preventDefault();

                if (isEditingThis) {
                  const educationEditorStore =
                    educationSegmentStore.educationEditorStore;

                  onCveEducationEditorCloseButtonClick({
                    educationSegmentStore,
                    educationEditorStore,
                    queryClient,
                    onSaveApiCallStart: () => {
                      savingUsingToggleButton.set(true);
                    },
                    onSaveApiCallEnd: () => {
                      savingUsingToggleButton.set(false);
                    },
                  });
                } else {
                  redirect(null, {
                    userInfo,
                    mergeQs: { [CVPSearchParams.cvpedu]: cveEdu.id },
                  });
                }

                return false;
              })}
            />
          </>
        );
      }}
    />
  );
};

export const CVEditEducationListStyles = styled(CVEditListStyles)`
  &.cve-education-list {
    .cve-education-listing-education-type {
      font-weight: bold;
    }
  }
`;
